import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, Plus, GitCompare, Trash2, ExternalLink } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { errorMessage } from '../errors';
import { qualificationApi, OVERALL_STATUS_LABELS, statusColor } from '../qualificationApi';
import QualificationWizard from './QualificationWizard';
import QualificationResults from './QualificationResults';
import ScenarioCompare from './ScenarioCompare';
import { formatDate } from '../kit/time';

const when = (iso) => iso ? formatDate(iso) : '—';

function maxOver(scenario) {
  const candidates = (scenario.result?.programs || []).filter((p) => p.status !== 'not_qualified');
  if (candidates.length === 0) return null;
  return Math.max(...candidates.map((p) => p.max_purchase_price));
}

export default function ContactQualification({ contactId, loanId }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [scenarios, setScenarios] = useState(null);
  const [selected, setSelected] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [mode, setMode] = useState(null); // null | 'new' | 'edit' | 'compare'
  const [editing, setEditing] = useState(null); // the scenario open in the wizard when mode is 'edit'

  const load = useCallback(() => {
    qualificationApi.listScenarios(contactId, loanId)
      .then((list) => setScenarios(Array.isArray(list) ? list : []))
      .catch((e) => { toast({ title: 'Could not load scenarios', description: errorMessage(e) }); setScenarios([]); });
  }, [contactId, loanId]);

  useEffect(() => { load(); }, [load]);

  const showAll = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('loan_id');
    setSearchParams(next, { replace: true });
  };

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id)
      ? prev.filter((x) => x !== id)
      : (prev.length >= 3 ? prev : [...prev, id]));
  };

  const onSaved = () => {
    setMode(null);
    load();
  };

  // The server recomputes the result on update; swap the returned scenario into its row.
  const onUpdated = (updated) => {
    setScenarios((prev) => (prev || []).map((s) => (s.scenario_id === updated.scenario_id ? updated : s)));
    setEditing(null);
    setMode(null);
  };

  const startEdit = (scenario) => {
    setEditing(scenario);
    setMode('edit');
  };

  const stopEdit = () => {
    setEditing(null);
    setMode(null);
  };

  const del = async (scenario) => {
    const ok = await confirmDialog({
      title: `Delete "${scenario.name}"?`,
      description: 'This scenario will be permanently removed.',
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await qualificationApi.deleteScenario(scenario.scenario_id);
      toast({ title: 'Scenario deleted' });
      setSelected((prev) => prev.filter((x) => x !== scenario.scenario_id));
      load();
    } catch (e) {
      toast({ title: 'Could not delete scenario', description: errorMessage(e) });
    }
  };

  if (mode === 'new') {
    return (
      <QualificationWizard contactId={contactId} loanId={loanId} onSaved={onSaved} onCancel={() => setMode(null)} />
    );
  }

  if (mode === 'edit' && editing) {
    return (
      <QualificationWizard
        key={editing.scenario_id}
        contactId={contactId}
        loanId={loanId}
        scenario={editing}
        onSaved={(created) => { stopEdit(); onSaved(created); }}
        onUpdated={onUpdated}
        onCancel={stopEdit}
      />
    );
  }

  if (scenarios === null) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400" data-testid="qualification-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading scenarios...
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="contact-qualification">
      {loanId && (
        <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-sm text-slate-600">
          <span>Showing scenarios for loan {loanId}</span>
          <button type="button" onClick={showAll} className="text-xs underline" data-testid="qualification-show-all">Show all</button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setMode('new')} className="btn-gold" data-testid="qualification-new-scenario">
          <Plus size={14} /> New scenario
        </button>
        <button type="button" onClick={() => setMode('compare')} disabled={selected.length < 2}
          className="btn-outline disabled:opacity-40" data-testid="qualification-compare">
          <GitCompare size={14} /> Compare
        </button>
      </div>

      {mode === 'compare' && (
        <ScenarioCompare ids={selected} onClose={() => setMode(null)} />
      )}

      {scenarios.length === 0 ? (
        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-12 text-center text-slate-400" data-testid="qualification-empty">
          <p className="text-sm">No scenarios yet</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-100" data-testid="scenario-list">
          {scenarios.map((s) => {
            const total = s.result?.programs?.length || 0;
            const qualified = (s.result?.programs || []).filter((p) => p.status !== 'not_qualified').length;
            const maxPurchase = maxOver(s);
            return (
              <div key={s.scenario_id} data-testid={`scenario-row-${s.scenario_id}`}>
                <div className="flex items-center gap-3 px-4 py-3">
                  <input type="checkbox" checked={selected.includes(s.scenario_id)}
                    onChange={() => toggleSelect(s.scenario_id)}
                    disabled={!selected.includes(s.scenario_id) && selected.length >= 3}
                    data-testid={`scenario-select-${s.scenario_id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: '#070B5F' }}>{s.name}</div>
                    <div className="text-xs text-slate-500">{when(s.created_at)} · {qualified}/{total} programs qualified{maxPurchase != null ? ` · max ${Number(maxPurchase).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}` : ''}</div>
                  </div>
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusColor(s.result?.overall_status)}`}>
                    {OVERALL_STATUS_LABELS[s.result?.overall_status] || s.result?.overall_status}
                  </span>
                  {s.loan_id && (
                    <Link to={`/portal/loans/${s.loan_id}`} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
                      <ExternalLink size={11} /> Loan
                    </Link>
                  )}
                  <button type="button" onClick={() => setExpanded(expanded === s.scenario_id ? null : s.scenario_id)}
                    className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50" data-testid={`scenario-view-${s.scenario_id}`}>
                    View
                  </button>
                  <button type="button" onClick={() => startEdit(s)}
                    className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50" data-testid={`scenario-edit-${s.scenario_id}`}>
                    Edit
                  </button>
                  <button type="button" onClick={() => del(s)}
                    className="text-slate-400 hover:text-red-500" data-testid={`scenario-delete-${s.scenario_id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
                {expanded === s.scenario_id && (
                  <div className="px-4 pb-4">
                    <QualificationResults result={s.result} scenario={s} loanId={loanId} contactId={contactId} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
